<template>
    <section name="course-menu" class="gap-md flex flex-col">
        <div class="gap-sm flex flex-col">
            <p class="text-(--text-muted)">{{ getFriendlyTimeFromSeconds(course.duration) }}</p>

            <h1>{{ course.title }}</h1>
            <p>{{ course.description }}</p>
        </div>
        <div>
            <ol class="px-sm py-sm rounded-md border border-(--border-subtle)">
                <li
                    v-for="(item, index) in sortedSections"
                    :key="item.title"
                    class="flex cursor-pointer flex-col"
                    :class="{
                        'mt-md': index !== 0,
                        'mb-md': index !== course.sections.length - 1,
                    }"
                >
                    <div class="gap-xs lg:pl-sm flex items-center font-bold">
                        <i
                            class="i-small-arrow-down"
                            :class="{ '-rotate-90': !expandedIndices.includes(index) }"
                            @click="toggleExpand(index)"
                        />
                        <h2 @click="$emit('updateSection', item)">{{ index + 1 }} . {{ item.title }}</h2>
                    </div>
                    <ul v-show="expandedIndices.includes(index)" class="pt-4">
                        <li
                            v-for="subItem in item.contents"
                            :key="subItem.content_id"
                            class="py-2xs rounded-sm pl-[0.4rem] transition-colors lg:pl-[2.4rem]"
                            :class="{
                                'bg-(--surface-highlight)': activeContentId === subItem.content_id,
                                'text-(--text-disabled)': completedContentIds.includes(subItem.content_id),
                                hidden: !subItem.title,
                            }"
                        >
                            <div class="gap-sm flex items-center">
                                <span class="p-xs flex h-8 w-8 items-center justify-center rounded-sm bg-(--tag-primary)">
                                    <i
                                        :class="{
                                            'i-book': subItem.type.name !== 'video',
                                            'i-play': subItem.type.name === 'video',
                                        }"
                                    />
                                </span>

                                <p class="font-bold">
                                    {{ subItem.title.charAt(0).toUpperCase() + subItem.title.slice(1) }}
                                </p>
                            </div>
                        </li>
                        <li
                            v-for="subQuiz in item.quizes"
                            :key="subQuiz.quiz_id"
                            class="py-2xs rounded-sm pl-[0.4rem] transition-colors lg:pl-[2.4rem]"
                            :class="{
                                'bg-(--surface-highlight)': activeContentId === subQuiz.quiz_id,
                                'text-(--text-disabled)': completedContentIds.includes(subQuiz.quiz_id),
                            }"
                            :data-id="subQuiz.quiz_id"
                        >
                            <div class="gap-sm flex items-center">
                                <span class="p-xs flex h-8 w-8 items-center justify-center rounded-sm bg-(--tag-primary)">
                                    <i class="i-book" />
                                </span>
                                <div class="gap-xs flex flex-col">
                                    <p class="font-bold">
                                        {{ subQuiz.title.charAt(0).toUpperCase() + subQuiz.title.slice(1) }}
                                    </p>
                                </div>
                            </div>
                        </li>
                    </ul>
                </li>
            </ol>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useLMSStore } from '@/stores/lms';
import type { PropType } from 'vue';
import type { Course, Section } from '@/stores/store-interfaces';
import { getFriendlyTimeFromSeconds } from '@/stores/helpers';

const lmsStore = useLMSStore();
const expandedIndices = ref<number[]>([]);
const activeContentId = ref<string | null>(null);
const completedContentIds = ref<string[]>([]);

defineEmits<{ updateSection: [section: Section] }>();

const props = defineProps({
    course: {
        type: Object as PropType<Course>,
        required: true,
    },
    activeSection: {
        type: Object as PropType<Section>,
        required: true,
    },
});

const observer = new IntersectionObserver(
    (entries) => {
        entries.forEach((entry) => {
            const id = entry.target.getAttribute('content-id')!;
            if (entry.isIntersecting) {
                activeContentId.value = id;
                if (!completedContentIds.value.includes(id)) {
                    completedContentIds.value.push(id);
                }
            }
        });
    },
    { threshold: 0.5 }, // Adjust the threshold for better results
);

const sortedSections = computed(() => {
    return props.course.sections.slice().sort((a, b) => a.order! - b.order!);
});

const toggleExpand = (index: number) => {
    if (expandedIndices.value.includes(index)) {
        expandedIndices.value = expandedIndices.value.filter((item) => item !== index);
    } else {
        expandedIndices.value.push(index);
    }
};

// Intersection Observer logic
onMounted(() => {
    const elements = document.getElementsByName('contentRefs');
    elements.forEach((element) => observer.observe(element));
    toggleExpand(Number.parseInt(lmsStore.activeSectionIndex));

    return () => observer.disconnect(); // Cleanup observer on unmount
});

watch(
    () => props.activeSection,
    (newVal) => {
        const index = props.course.sections.findIndex((section) => section === newVal);
        if (index !== -1) {
            expandedIndices.value = [];
            toggleExpand(index);
        }
    },
);
</script>
